/**
 * Fondo de la aplicación: estilo y movimiento.
 *
 * Sustituye la rejilla fija que se veía en todas las pantallas por un campo de
 * degradados difuminados que cambia de tono con la paleta (`styles/background.css`).
 *
 * Escribe dos atributos en <html>, que es lo único que lee el CSS:
 *
 *   data-bg        → aurora | mesh | plain
 *   data-bg-motion → off | soft | lively
 *
 * La preferencia es del navegador, como el resto del aspecto (`theme/appearance.js`):
 * no hay nada que guardar en el servidor porque no cambia datos de la empresa.
 */

export const BG_STYLE_KEY = 'cmdb-bg';
export const BG_MOTION_KEY = 'cmdb-bg-motion';

export const BG_STYLES = [
  {
    id: 'aurora',
    name: 'Aurora',
    tagline: 'Degradados difuminados que cambian con la paleta',
  },
  {
    id: 'mesh',
    name: 'Malla',
    tagline: 'La rejilla de siempre, ahora opcional y más tenue',
  },
  {
    id: 'plain',
    name: 'Liso',
    tagline: 'Solo el color de fondo, sin textura',
  },
];

export const BG_MOTIONS = [
  { id: 'off', name: 'Quieto' },
  { id: 'soft', name: 'Suave' },
  { id: 'lively', name: 'Vivo' },
];

export const DEFAULT_BG_STYLE = 'aurora';
export const DEFAULT_BG_MOTION = 'soft';

const isStyle = (id) => BG_STYLES.some((s) => s.id === id);
const isMotion = (id) => BG_MOTIONS.some((m) => m.id === id);

/** Lo elegido en este navegador; sin nada guardado, los valores por defecto. */
export function readStoredBackground() {
  let style = DEFAULT_BG_STYLE;
  let motion = DEFAULT_BG_MOTION;
  try {
    const storedStyle = localStorage.getItem(BG_STYLE_KEY);
    if (storedStyle && isStyle(storedStyle)) style = storedStyle;
    const storedMotion = localStorage.getItem(BG_MOTION_KEY);
    if (storedMotion && isMotion(storedMotion)) motion = storedMotion;
  } catch {
    // Almacenamiento bloqueado: se queda con los valores por defecto.
  }
  return { style, motion };
}

/** Escribe el fondo en <html>. Devuelve lo aplicado. */
export function applyBackground({ style, motion } = {}) {
  const root = document.documentElement;
  const finalStyle = isStyle(style) ? style : DEFAULT_BG_STYLE;
  const finalMotion = isMotion(motion) ? motion : DEFAULT_BG_MOTION;

  root.setAttribute('data-bg', finalStyle);
  root.setAttribute('data-bg-motion', finalMotion);

  return { style: finalStyle, motion: finalMotion };
}

/** Guarda la elección y la aplica al instante. */
export function saveBackground({ style, motion } = {}) {
  const applied = applyBackground({
    style: style || readStoredBackground().style,
    motion: motion || readStoredBackground().motion,
  });
  try {
    localStorage.setItem(BG_STYLE_KEY, applied.style);
    localStorage.setItem(BG_MOTION_KEY, applied.motion);
  } catch {
    // Sin persistencia, pero el cambio se ve en esta sesión.
  }
  return applied;
}

/** Fondo inicial, antes de que React pinte (se llama desde `index.js`). */
export function bootBackground() {
  return applyBackground(readStoredBackground());
}
